import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const cardVariants = cva(
  'tw-bg-card tw-text-card-foreground tw-flex tw-flex-col tw-rounded-xl tw-border tw-shadow-sm',
  {
    variants: {
      variant: {
        default: 'tw-gap-6 tw-py-6',
        compacted: 'tw-gap-0 tw-py-0',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const cardHeaderVariants = cva(
  'tw-@container/card-header tw-grid tw-auto-rows-min tw-grid-rows-[auto_auto] tw-has-data-[slot=card-action]:grid-cols-[1fr_auto] tw-[.border-b]:pb-6',
  {
    variants: {
      variant: {
        default: 'tw-gap-1.5 tw-px-6 tw-items-start',
        compacted: 'tw-gap-1 tw-px-5 tw-min-h-14 tw-content-center',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const cardContentVariants = cva('', {
  variants: {
    variant: {
      default: 'tw-px-6',
      compacted: 'tw-px-5 tw-pb-3.5',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const cardFooterVariants = cva(
  'tw-flex tw-items-center tw-[.border-t]:pt-6',
  {
    variants: {
      variant: {
        default: 'tw-px-6',
        compacted: 'tw-px-5',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type CardContextType = {
  variant?: VariantProps<typeof cardVariants>['variant'];
};

const CardContext = React.createContext<CardContextType>({});

const Card = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & VariantProps<typeof cardVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <CardContext.Provider value={{ variant }}>
      <div
        ref={ref}
        data-slot="card"
        className={cn(cardVariants({ variant, className }))}
        {...props}
      />
    </CardContext.Provider>
  );
});
Card.displayName = 'Card';

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & VariantProps<typeof cardHeaderVariants>
>(({ className, variant: variantProp, ...props }, ref) => {
  const context = React.useContext(CardContext);
  const variant = variantProp ?? context.variant;

  return (
    <div
      ref={ref}
      data-slot="card-header"
      className={cn(cardHeaderVariants({ variant, className }))}
      {...props}
    />
  );
});
CardHeader.displayName = 'CardHeader';

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn('tw-leading-none tw-font-semibold', className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('tw-text-muted-foreground tw-text-sm', className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'tw-col-start-2 tw-row-span-2 tw-row-start-1 tw-self-start tw-justify-self-end',
        className,
      )}
      {...props}
    />
  );
}

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & VariantProps<typeof cardContentVariants>
>(({ className, variant: variantProp, ...props }, ref) => {
  const context = React.useContext(CardContext);
  const variant = variantProp ?? context.variant;

  return (
    <div
      ref={ref}
      data-slot="card-content"
      className={cn(cardContentVariants({ variant, className }))}
      {...props}
    />
  );
});
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & VariantProps<typeof cardFooterVariants>
>(({ className, variant: variantProp, ...props }, ref) => {
  const context = React.useContext(CardContext);
  const variant = variantProp ?? context.variant;

  return (
    <div
      ref={ref}
      data-slot="card-footer"
      className={cn(cardFooterVariants({ variant, className }))}
      {...props}
    />
  );
});
CardFooter.displayName = 'CardFooter';

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
};
